import { showAlert } from 'BMapsSrc/utils';
import { offerWatermapDerivation } from 'BMapsSrc/cmds/watermapDerivation';
import { UserActions } from 'BMapsCmds';
import { openScreeningSidePanel } from 'BMapsUI/dialog/ScreeningPane/ScreeningSidePanel';
import { openFreeEnergySidePanel } from 'BMapsUI/dialog/FreeEnergyPane/FreeEnergySidePanel';
import { openMdSidePanel } from 'BMapsUI/dialog/MdPane/MdSidePanel';
import { App } from '../../BMapsApp';
import { EnergyInfo } from '../../model/energyinfo';
import {
    WaterStates, HBondStates, DipoleStates, BindingSiteSurfaceStates,
} from '../../model/DisplayState';
import { imageSrc } from '../ui_utils';
import { HydrogenDisplayOptions } from '../../util/display_utils';

/**
 * Perform actions requested by the RightControlPanel
 * @param {string} actionId
 */
export function doRightControlPanelAction(actionId) {
    switch (actionId) {
        case 'views-ligand':
            // When there are selected atoms, "Ligand View" zooms into them, instead of a compound.
            // This is where the binding site is updated for the selected atoms.
            App.Workspace.calculateAndSaveBindingSite();
            UserActions.SetView('ligand');
            break;
        case 'views-protein':
            UserActions.SetView('protein');
            break;
        case 'views-publication':
            UserActions.SetView('publication');
            break;
        case 'views-recenter':
            UserActions.RecenterView();
            break;
        case 'views-reset':
            UserActions.ResetView();
            break;
        case WaterStates.none:
        case WaterStates.crystal:
        case WaterStates.computed:
        case WaterStates.all:
            UserActions.SetWaterView(actionId);
            break;
        case 'hbondson':
        case 'hbondsoff':
            UserActions.SetHBonds(actionId === 'hbondson' ? HBondStates.normal : HBondStates.none);
            break;
        case 'weakhbondson':
        case 'weakhbondsoff':
            UserActions.SetHBonds(actionId === 'weakhbondson' ? HBondStates.includeWeak : HBondStates.none);
            break;
        case 'pipistackingon':
        case 'pipistackingoff':
            UserActions.SetPiPiStacking(actionId === 'pipistackingon');
            break;
        case 'dipoleson':
        case 'dipolesoff':
            UserActions.SetDipoles(actionId === 'dipoleson' ? DipoleStates.normal : DipoleStates.none);
            break;
        case 'protsolvationon':
            //--- check if info available, do alert otherwise
            UserActions.SetBindingSiteSurface(BindingSiteSurfaceStates.ddgs);
            break;
        case 'hydrophobicon':
            UserActions.SetBindingSiteSurface(BindingSiteSurfaceStates.hydrophobicity);
            break;
        case 'protsolvationoff':
        case 'hydrophobicoff':
            UserActions.SetBindingSiteSurface(BindingSiteSurfaceStates.off);
            break;
        case 'ligandsolvationoff':
        case 'ligandsolvationon':
            UserActions.SetLigandSolvation(actionId === 'ligandsolvationon');
            break;
        case 'bfactoron': case 'bfactoroff':
            UserActions.ShowBfactor(actionId === 'bfactoron');
            break;
        case 'hotspotson':
        case 'hotspotsoff':
            UserActions.SetHotspots(actionId === 'hotspotson');
            break;
        case 'nohydrogens':
            UserActions.SetHydrogenView(HydrogenDisplayOptions.none);
            break;
        case 'polarhydrogens':
            UserActions.SetHydrogenView(HydrogenDisplayOptions.polar);
            break;
        case 'allhydrogens':
            UserActions.SetHydrogenView(HydrogenDisplayOptions.all);
            break;
        case 'dockcompound':
            UserActions.OpenDock();
            break;
        case 'watermap':
            UserActions.ShowFragmentPane('water');
            break;
        case 'fragmap':
            UserActions.ShowFragmentPane('fragment');
            break;
        case 'clustermap':
            UserActions.ShowFragmentPane('clustering');
            break;
        case 'newligand':
            UserActions.OpenSketcher();
            break;
        case 'editligand':
            UserActions.OpenSketcher(App.Workspace.getActiveCompound());
            break;
        case 'energyminimize':
            UserActions.RequestMinimize();
            break;
        case 'calculateEnergies':
            UserActions.ShowInspectorTab('energies');
            UserActions.GetEnergies(App.Workspace.getSelectedOrActiveCompounds());
            break;
        case 'terminalReplacement':
            showAlert(
                'Terminal replacement',
                [
                    "Right-click a compound's terminal group or hydrogen in the 3D workspace to view terminal replacement options.",
                    'Hold down the R key to view all hydrogens and functional groups available for replacement.',
                ]
            );
            break;
        case 'fragmentGrow':
            showAlert(
                'Fragment growing',
                [
                    'Right-click a compound atom in the 3D workspace and choose "Grow with fragment" to view nearby candidates for growing, ranked by their simulated binding affinity with the protein.',
                    'The cyan arrow indicates the direction of the grow. The compound portion pointed to by the arrow will be replaced by the fragment. Use Ctrl-right-click to reverse direction.',
                    'If there is more than one bond to grow along, add Alt or Shift to select a different direction.',
                ]
            );
            break;
        case 'SearchFragmentMaps':
            showAlert(
                'Search Fragment Maps',
                [
                    'To view fragments from a simulated fragment map, right-click any atom and choose "Search Nearby Fragments" or "Fragment Data Query." The resulting fragments can be brought into the workspace as starting points for new compounds.',
                    'To search the fragment map for compound modification options, right-click a compound atom and choose "Grow with Fragment."',
                    'To get a rough summary of a fragment map, click on a fragment in the left-hand Fragments selector.',
                ]
            );
            break;
        case 'screening':
            openScreeningSidePanel('new_screen_tab', { selectedCompounds: App.Workspace.getSelectedOrActiveCompounds() });
            break;
        case 'freeEnergy':
            openFreeEnergySidePanel('new_fep_tab', { selectedCompounds: App.Workspace.getSelectedOrActiveCompounds() });
            break;
        case 'molecularDynamics':
            openMdSidePanel('new_md_tab', { selectedCompounds: App.Workspace.getSelectedOrActiveCompounds() });
            break;
        case 'generateCmpds':
            UserActions.OpenImport('compound_generation_tab', { selectedCompounds: App.Workspace.getSelectedOrActiveCompounds() });
            break;
        case 'hidden':
        case 'wireframe':
        case 'sticks':
        case 'ballandstick':
        case 'spacefill':
        case 'cartoon':
        case 'surface':
            //--- difference between lines and thick lines as different styles?
            UserActions.SetDisplayStyle(actionId);
            break;
        default:
    }
}

/**
 * Return whether an action from the RightControlPanel is available.
 * SHOWS a descriptive alert if the action is not available.
 * @param {string} actionId
 * @returns {boolean}
 *
 * These conditions should perhaps be somewhere else:
 *   1) These are related to business logic / policy more so than the UI
 *   2) The UserAction infrastructure should be extended to be able to ask if
 *      an action is possible.
 */
export function validateRightControlPanelAction(actionId) {
    // Validate whether an operation or style is enabled (i.e. the data required is
    // available).  Returns a boolean (true if ok, false if not).
    const loadedProteins = App.Workspace.getLoadedProteins();
    let firstProtein = null;
    let proteinCaseData = null;
    for (const p of loadedProteins) {
        const caseData = App.Workspace.lookupCaseData(p);
        if (caseData.getProteinChains().length > 0) {
            firstProtein = p;
            proteinCaseData = caseData;
            break;
        }
    }
    const protein = !!firstProtein;
    const noProtein = protein === false;
    const cmpd = App.Workspace.getActiveCompound();
    const selectedOrActive = App.Workspace.getSelectedOrActiveCompounds();
    const noCmpd = cmpd == null;
    const fragments = proteinCaseData ? proteinCaseData.areFragmentsAvailable() : false;
    const noFragments = !fragments;

    const failIfMinimizing = (op, title, cmpds=[cmpd]) => {
        const minimizing = [];
        cmpds.forEach((c) => { if (c.isMinimizing()) minimizing.push(c); });
        if (minimizing.length > 0) {
            showAlert(
                title,
                `Please wait for energy minimization to finish before attempting to ${op} ${minimizing.join(', ')}.`,
            );
            return true;
        }
        return false;
    };

    switch (actionId) {
        case 'views-ligand':
            if (protein && noCmpd) {
                if (App.Workspace.getSelectedAtoms().length === 0) {
                    showAlert('Ligand View', 'Select some atoms to determine a center for Ligand View.');
                    return false;
                }
            }
            if (noCmpd && noProtein) {
                showAlert('View', 'No compound to view.');
                return false;
            }
            break;
        case 'views-protein':
        case 'views-publication':
            // Condition: A protein
            if (noProtein) {
                showAlert('View', 'No protein to view.');
                return false;
            }
            break;
        case 'export':
            // Condition: A protein OR a compound
            if (noProtein && noCmpd) {
                showAlert('Export', 'Nothing available yet to export.');
                return false;
            }
            break;
        case 'selection':
            // Condition: A protein OR a compound
            if (noProtein && noCmpd) {
                showAlert('Atom Selection', 'Nothing available yet to select.');
                return false;
            }
            break;
        case 'dockcompound':
            // Condition: An active compound (and user permissions)
            if (!App.AllPermissions.isAllowed('dock')) {
                return false;
            }
            if (noCmpd) {
                showAlert('Docking', 'No compound available for docking. Try importing a compound.');
                return false;
            }
            if (!cmpd.canDock) {
                showAlert('Docking', 'Docking peptide ligands is not supported.');
                return false;
            }
            if (noProtein) {
                showAlert('Docking', 'There is no solute to dock to. Try choosing a protein and re-importing your compound.');
                return false;
            }
            if (failIfMinimizing('dock', 'Docking')) {
                return false;
            }
            break;
        case 'watermap':
        case 'fragmap':
        case 'clustermap':
            if (noProtein) {
                showAlert('Fragment simulation', 'Select a protein to run simulations.');
                return false;
            }
            break;

        case 'editligand':
        case 'terminalReplacement':
        case 'fragmentGrow':
            // Condition: An active compound
            if (noCmpd) {
                showAlert('Edit Compound', 'No compound to edit.');
                return false;
            }
            break;
        case 'ligandsolvationoff':
            break;
            // Condition: An active compound WITH ddGs energy info
        case 'ligandsolvationon':
            if (cmpd) {
                if (!cmpd.energyTypeAvailable(EnergyInfo.Types.ddGs)) {
                    showAlert(
                        'Ligand Desolvation Highlights',
                        [
                            'Compound energies not available.',
                            "Please click 'Calculate Energies' in order to view ligand desolvation highlights.",
                        ]
                    );
                    return false;
                }
            } else {
                showAlert('Ligand Desolvation Highlights', 'No ligand available yet.');
                return false;
            }
            break;
        case 'protsolvationoff':
            break;
        case 'protsolvationon':
            // Condition: A protein AND an active compound WITH ddgs Energy info
            if (noProtein) {
                showAlert(
                    'Protein Desolvation Surface Highlights',
                    'No protein available yet.',
                );
                return false;
            }

            if (cmpd) {
                if (!cmpd.energyTypeAvailable(EnergyInfo.Types.ddGs)) {
                    showAlert(
                        'Protein Desolvation Surface Highlights',
                        [
                            'Compound energies not available.',
                            "Please click 'Calculate Energies' in order to view protein desolvation highlighting.",
                        ]
                    );
                    return false;
                }
            } else {
                showAlert(
                    'Protein Desolvation Surface Highlights',
                    'No compound available yet for desolvation highlighting .',
                );
                return false;
            }
            break;
        case 'hydrophobicon':
            // Condition: A protein
            if (noProtein) {
                showAlert(
                    'Protein Hydrophobicity Highlight',
                    'No protein available yet.',
                );
                return false;
            }
            break;
        case 'energyminimize':
        case 'calculateEnergies':
            if (noCmpd) {
                showAlert(
                    'Minimization',
                    'Energy minimization requires a compound. Please make sure you have loaded a compound.',
                );
                return false;
            }
            if (failIfMinimizing('minimize', 'Minimization', selectedOrActive)) {
                return false;
            }
            break;
        case 'SearchFragmentMaps':
            if (noProtein || noFragments) {
                showAlert(
                    'Search Fragment Maps',
                    'Please choose a protein with simulation data in order to view and search fragment maps.',
                );
                return false;
            }
            break;
        case WaterStates.none:
        case WaterStates.crystal: // Condition: Crystal waters
        case WaterStates.computed: // Condition: BMaps waters
        case WaterStates.all: { // Condition: Crystal waters AND BMaps waters (should be revisited)
            const hasCrystalWaters = App.Workspace.getAllCrystalWaterAtoms().length > 0;
            const hasComputedWaters = App.Workspace.getAllComputedWaterAtoms().length > 0;
            // These buttons act on every displayed protein, so offer for every case that
            // could have a map derived -- not only when NO case has one. Testing
            // hasComputedWaters alone meant that once any case had a map, a second case with
            // water data and no map was never offered. A per-protein choice belongs to that
            // case's Water row in the Fragments tree, not here.
            const derivable = App.Workspace.allCaseData().filter((cd) => cd.canDeriveWatermap());
            // Not awaited: this validator is called synchronously from the panel, so the
            // offer runs on its own and switches the view itself once a map arrives.
            if (actionId === WaterStates.crystal && !hasCrystalWaters) {
                showAlert('Water Display', 'No crystal waters available.');
                return false;
            } else if (actionId === WaterStates.computed
                    && (!hasComputedWaters || derivable.length > 0)) {
                if (!hasComputedWaters && derivable.length === 0) {
                    showAlert('Water Display', 'No computed BMaps waters available.');
                    return false;
                }
                offerWatermapDerivation(derivable);
                // Let an existing map display while the rest are offered.
                return hasComputedWaters;
            } else if (actionId === WaterStates.all) {
                if (!hasCrystalWaters && !hasComputedWaters && derivable.length === 0) {
                    showAlert('Water Display', 'No waters available.');
                    return false;
                }
                if (!hasCrystalWaters && !hasComputedWaters) {
                    // Nothing to show yet, but something to derive: the view switches by
                    // itself if the offer is accepted and a map lands.
                    offerWatermapDerivation(derivable, { targetState: WaterStates.all });
                    return false;
                }
                // Here at least one kind exists; show it and offer for the rest.
                if (!hasCrystalWaters) {
                    showAlert('Water Display', 'No crystal waters available.');
                }
                if (derivable.length > 0) {
                    offerWatermapDerivation(derivable, { targetState: WaterStates.all });
                } else if (!hasComputedWaters) {
                    // Crystal waters only, with no water data to derive from. Without this
                    // the view switches to "all", shows the crystal waters, and never says
                    // why there are no BMaps ones -- which the old code did tell the user.
                    showAlert('Water Display', 'No computed BMaps waters available.');
                }
            }
            break;
        }
        default:
            break;
    }

    return true;
}

export function imageSrcForStyle(styleWithPng) {
    return imageSrc(`${styleWithPng}.png`);
}

/**
 * What the Right Control Panel's controls should show for the current display state: each
 * toggle's on/off by its id, and for a drawer that shows a display state option (see stateKey
 * in RightControlPanel.jsx), the id of its active option.
 */
export function processViewState() {
    const viewState = App.Workspace?.displayState;
    const updates = {};

    if (!viewState) {
        return updates;
    }

    switch (viewState.hydrogens) {
        case HydrogenDisplayOptions.none: updates.hydrogens = 'nohydrogens'; break;
        case HydrogenDisplayOptions.polar: updates.hydrogens = 'polarhydrogens'; break;
        case HydrogenDisplayOptions.all: updates.hydrogens = 'allhydrogens'; break;
        default:
            console.warn(`processViewState: unhandled hydrogen state: ${viewState.hydrogens}`);
    }

    updates.hbonds = viewState.hbonds === HBondStates.normal;
    updates.weakhbonds = viewState.hbonds === HBondStates.includeWeak;
    updates.pipistacking = !!viewState.pipistacking;
    updates.dipoles = viewState.dipoles !== DipoleStates.none;
    updates.hotspots = !!viewState.hotspots;
    updates.ligandsolvation = !!viewState.functionalGroupHighlight;
    switch (viewState.bindingSiteSurfaceState) {
        case BindingSiteSurfaceStates.off:
            updates.protsolvation = false;
            updates.hydrophobic = false;
            break;
        case BindingSiteSurfaceStates.ddgs:
            updates.protsolvation = true;
            updates.hydrophobic = false;
            break;
        case BindingSiteSurfaceStates.hydrophobicity:
            updates.hydrophobic = true;
            updates.protsolvation = false;
            break;
        default:
            console.warn(`onViewStateChanged: unhandled binding site surface state: ${viewState.bindingSiteSurfaceState}`);
    }

    return updates;
}
